/**
 * Dosya detayındaki Belgeler bölümünün "Masraflar ve Ödemeler" alanı.
 *
 * - BelgeAlaniSekmeleri: Belgeler | Masraflar ve Ödemeler
 * - OdemeSekmeleri: Masraflar | Gelen Ödemeler | Müvekkile Ödenen | Makbuzlar
 * - OdemeListesi: masraf kayıtlarından gelen sekmelerin tablosu
 *
 * Makbuzlar sekmesinde belge tablosunun kendisi (yalnız makbuzlar)
 * gösterilir; bu dosya yalnız sekmeleri ve ödeme listelerini çizer.
 */
import React from 'react';
import Icon from '../Common/Icon';
import YatayKaydirma from '../Common/YatayKaydirma';
import { formatDateOnly } from '../../utils/dateUtils';
import { toplamTutar } from '../../utils/odemeSiniflandir';
import './FaturaOdemeler.css';

export const paraYaz = (n) => new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY' }).format(Number(n) || 0);

export function BelgeAlaniSekmeleri({ alan, onAlan, belgeSayisi, odemeSayisi }) {
  return (
    <div className="fo-alan" role="tablist" aria-label="Belge alanı" data-aktif={alan}>
      <span className="fo-alan-gosterge" aria-hidden="true" />
      <button
        type="button"
        role="tab"
        aria-selected={alan === 'belgeler'}
        className={`fo-alan-dugme${alan === 'belgeler' ? ' aktif' : ''}`}
        onClick={() => onAlan('belgeler')}
      >
        <Icon name="document" size={16} /> Belgeler <span className="fo-sayi">{belgeSayisi}</span>
      </button>
      <button
        type="button"
        role="tab"
        aria-selected={alan === 'odemeler'}
        className={`fo-alan-dugme${alan === 'odemeler' ? ' aktif' : ''}`}
        onClick={() => onAlan('odemeler')}
      >
        <Icon name="receipt" size={16} /> Masraflar ve Ödemeler <span className="fo-sayi">{odemeSayisi}</span>
      </button>
    </div>
  );
}

const SEKME_SIMGESI = { makbuz: 'receipt', gelen: 'income', muvekkile: 'wallet', masraf: 'expense' };

/* Belge yüklerken "Nereye Eklensin?" kutucukları. */
const HEDEFLER = [
  { anahtar: 'belgeler', ad: 'Belgeler', alt: 'Vekaletname, tutanak, rapor…', simge: 'document' },
  { anahtar: 'masraf', ad: 'Masraflar', alt: 'Fatura, fiş, harç makbuzu', simge: 'expense' },
  { anahtar: 'gelen', ad: 'Gelen Ödemeler', alt: 'Gelen ödeme dekontu', simge: 'income' },
  { anahtar: 'muvekkile', ad: 'Müvekkile Ödenen', alt: 'Müvekkile ödeme dekontu', simge: 'wallet', yalnizYonetici: true },
  { anahtar: 'makbuz', ad: 'Makbuzlar', alt: 'Serbest meslek makbuzu', simge: 'receipt' },
];

/**
 * secili: 'belgeler' | 'masraf' | 'gelen' | 'muvekkile' | 'makbuz' | null (seçilmedi)
 * oneri: dosya adından tahmin edilen yer (yalnız işaretlenir, seçimi kullanıcı yapar)
 */
export function BelgeHedefSecici({ secili, onSec, yonetici, disabled, oneri }) {
  return (
    <fieldset className={`fo-hedef${secili ? '' : ' secilmedi'}`} disabled={disabled}>
      <legend>Nereye Eklensin? <span className="fo-zorunlu">*</span></legend>
      <div className="fo-hedef-kutular" role="radiogroup" aria-label="Belgenin ekleneceği yer">
        {HEDEFLER.filter((h) => yonetici || !h.yalnizYonetici).map((h) => (
          <button
            key={h.anahtar}
            type="button"
            role="radio"
            aria-checked={secili === h.anahtar}
            className={`fo-hedef-kutu${secili === h.anahtar ? ' secili' : ''}`}
            onClick={() => onSec(h.anahtar)}
          >
            <Icon name={h.simge} size={18} />
            <span className="fo-hedef-ad">{h.ad}</span>
            <span className="fo-hedef-alt">{h.alt}</span>
            {!secili && oneri === h.anahtar && <span className="fo-hedef-oneri">önerilen</span>}
          </button>
        ))}
      </div>
      {!secili && <p className="fo-hedef-uyari">Yüklemeden önce belgenin nereye ekleneceğini seçin.</p>}
    </fieldset>
  );
}

/** sekmeler: [{ anahtar, ad, sayi, toplam }] */
export function OdemeSekmeleri({ sekme, onSekme, sekmeler }) {
  const aktif = sekmeler.find((s) => s.anahtar === sekme) || sekmeler[0];
  return (
    <div className="fo-odeme-ust">
      <div className="fo-sekmeler" role="tablist" aria-label="Masraflar ve ödemeler">
        {sekmeler.map((s) => (
          <button
            key={s.anahtar}
            type="button"
            role="tab"
            aria-selected={s.anahtar === aktif.anahtar}
            className={`fo-sekme${s.anahtar === aktif.anahtar ? ' aktif' : ''}`}
            onClick={() => onSekme(s.anahtar)}
          >
            <Icon name={SEKME_SIMGESI[s.anahtar] || 'list'} size={15} />
            {s.ad}
            <span className="fo-sayi">{s.sayi}</span>
          </button>
        ))}
      </div>
      <div className="fo-ozet" key={aktif.anahtar}>
        {aktif.anahtar === 'makbuz' ? (
          <span>{aktif.belge} makbuz</span>
        ) : (
          <>
            <span>{aktif.belge} belge</span>
            <span className="fo-ozet-ayrac" aria-hidden="true">·</span>
            <span>{aktif.kayit} tutar kaydı</span>
          </>
        )}
        <span className="fo-ozet-ayrac" aria-hidden="true">·</span>
        <span>Toplam <strong>{paraYaz(aktif.toplam)}</strong>{aktif.anahtar === 'makbuz' ? ' (net)' : ''}</span>
      </div>
    </div>
  );
}

const BOS_METIN = {
  gelen: 'Masraflar bölümünde henüz gelen ödeme kaydı yok.',
  muvekkile: 'Masraflar bölümünde müvekkile yapılan ödeme kaydı yok.',
  masraf: 'Masraflar bölümünde henüz masraf kaydı yok.',
};

/** Sekmenin altında: Masraflar bölümünde girilen tutarlar (masraf türüne göre). */
export function OdemeListesi({ sekme, satirlar, yukleniyor, onMasraflaraGit }) {
  if (yukleniyor) return <p className="fo-bos">Yükleniyor…</p>;
  return (
    <div className="fo-icerik fo-kayitlar" key={sekme}>
      <h4 className="fo-alt-baslik">Masraflar bölümündeki tutarlar</h4>
      {satirlar.length === 0 ? (
        <div className="fo-bos-kutu">
          <p className="fo-bos">{BOS_METIN[sekme]}</p>
          <p className="fo-not">Tutarlar Masraflar bölümünden, masraf türü seçilerek girilir; burada kendiliğinden görünür.</p>
        </div>
      ) : (
        <YatayKaydirma className="fo-tablo-kutu">
          <table className="fo-tablo">
            <thead>
              <tr>
                <th>Tarih</th>
                <th>Tür</th>
                <th className="fo-sag">Tutar</th>
                <th>Açıklama</th>
              </tr>
            </thead>
            <tbody>
              {satirlar.map((s) => (
                <tr key={s.anahtar}>
                  <td className="fo-tarih">{s.tarih ? formatDateOnly(s.tarih) : '-'}</td>
                  <td>{s.tur}</td>
                  <td className="fo-sag fo-tutar">{paraYaz(s.tutar)}</td>
                  <td className="fo-aciklama">{s.aciklama || '-'}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr>
                <td colSpan={2}>Toplam</td>
                <td className="fo-sag fo-tutar">{paraYaz(toplamTutar(satirlar))}</td>
                <td />
              </tr>
            </tfoot>
          </table>
        </YatayKaydirma>
      )}
      {onMasraflaraGit && (
        <button type="button" className="fo-git" onClick={onMasraflaraGit}>
          Masraflar bölümünde düzenle →
        </button>
      )}
    </div>
  );
}
